import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { WorkbookInstance } from '@fortune-sheet/react';
import type { Op } from '@fortune-sheet/core';
import { toast } from 'react-toastify';
import { downloadAsCsv, toCelldataSheet } from './converters';
import { downloadAsXlsx, parseSheetBytes } from './parseSheets';
import { useLazySheets } from './useLazySheets';
import { useResolveSheets, LoadingStage } from './useResolveSheets';
import { FortuneSheet, ReadOnlyAddBand } from './types';
import { downloadBlobFromUrl } from '../../utils/downloadUtils';
import { isXlsxBuffer } from '../DocumentViewer/spreadsheetBytes';
import {
    countFilledCells,
    isReadOnlySize,
    MAX_EDITABLE_CELLS,
    READ_ONLY_SHORTCUT_SAVE_NOTICE,
    readOnlyNoticeMessage,
    readOnlySaveMessage,
    SheetReadOnlyAddError,
} from './sheetSizeGuard';
import { logSheetLoadFailure } from './sheetLoadTelemetry';
import { getDocument, getDocumentHistory, saveDocument } from '../../store/investor/investorApiHelper';
import {
    isSheetS3StorageEnabled,
    isSheetStorageRef,
    getSheetDownloadUrl,
    hasVersionHistory,
    uploadSheetVersion,
    SheetStorageRef,
} from './sheetStorage';
import { useHistoryDrawer } from '../HistorySideDrawer/useHistoryDrawer';
import { HistoryEntry } from '../HistorySideDrawer/HistorySideDrawer';
import { commitEditThen, getInternals } from './excel/workbookInternals';
import { useSheetCloseFlow } from './useSheetCloseFlow';

export type SavingPhase = 'idle' | 'serializing' | 'uploading' | 'saving';
export type AutosaveStatus = 'idle' | 'saving' | 'saved' | 'error';
export type { LoadingStage };

// Debounce window after the last workbook edit before a DRAFT autosave fires.
// Longer than Plate's 1s (useDocumentAutosave.AUTOSAVE_DEBOUNCE_MS): a sheet
// autosave re-serializes the ENTIRE workbook to xlsx and re-uploads it (not an
// incremental JSON diff like Plate), which is heavy for large workbooks — a
// longer window keeps rapid edit bursts from re-triggering that work on every tick.
const SHEET_AUTOSAVE_DEBOUNCE_MS = 10_000;

// How long the header's autosave indicator stays on "Saved" before decaying
// back to idle. Unlike Plate's headerSyncStatus (always visible once seeded),
// this widget only lights up around an actual save, so it needs its own decay.
const AUTOSAVE_STATUS_DECAY_MS = 3000;

// The close path's own wait for a device import's eager save
// (awaitPendingDocId) had no bound — a hung upload could leave × doing
// nothing forever, with no failure dialog and no feedback.
const AWAIT_PENDING_DOC_ID_CLOSE_TIMEOUT_MS = 15_000;

// Shared by handleSave/handleRename — the only two callers of
// awaitPendingDocIdBounded that throw on a timeout rather than handling it
// inline (runAutosave/flushPendingAutosave set autosaveStatus instead).
const PENDING_DOC_ID_TIMEOUT_MESSAGE = '[useSheetEditorDrawerLogic] timed out waiting for the pending document id';

/**
 * Saves the ORIGINAL file bytes (a read-only-band workbook is never re-serialised,
 * and a too-large file was never parsed). A cross-origin `<a download>` ignores
 * the `download` attribute's filename, so already-fetched bytes are saved as a
 * same-origin Blob instead; the bare `fileUrl` anchor is the fallback when the
 * bytes never made it into memory.
 */
const downloadOriginalBytes = (bytes: ArrayBuffer | null | undefined, fileUrl: string | undefined, downloadName: string): void => {
    if (bytes) {
        downloadBlobFromUrl(new Blob([bytes]), downloadName);
        return;
    }
    if (!fileUrl) return;
    const link = document.createElement('a');
    link.href = fileUrl;
    link.download = downloadName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
};

/**
 * A history preview mounts the Workbook read-only (allowEdit=false, pane open),
 * and Fortune-Sheet's read-only InputBox layout effect dispatches a context
 * update whose formula drain (groupValuesRefresh) rewrites `luckysheetfile` —
 * one of that effect's own dependencies. On a formula-heavy sheet the
 * execute→drain→re-fire chain exceeds React's nested-update limit at mount
 * ("Maximum update depth exceeded"). A committed version already carries every
 * computed value (`v`/`m`), so the preview COPY drops `f` and `calcChain` —
 * nothing left to execute, the drain never produces a patch, no loop. Only the
 * rendered preview is stripped: versionCacheRef keeps the full sheets, and
 * restore persists from those (plus storageOverride), at full fidelity.
 */
function stripFormulasForPreview(sheets: FortuneSheet[]): FortuneSheet[] {
    return sheets.map((sheet) => {
        const { calcChain, ...rest } = sheet;
        return {
            ...rest,
            celldata: sheet.celldata?.map((entry) => {
                if (!entry?.v || typeof entry.v !== 'object' || !('f' in entry.v)) return entry;
                const { f, ...cell } = entry.v;
                return { ...entry, v: cell };
            }),
        };
    });
}

export interface UseSheetEditorDrawerLogicArgs {
    open: boolean;
    fileName: string;
    /** In-memory sheets (import / composed). When provided, skips the URL fetch. */
    sheets?: FortuneSheet[];
    /** Presigned URL of the xlsx/csv file to edit (legacy fetch path). */
    fileUrl?: string;
    /** Resolved MIME type — drives xlsx vs csv parsing on the fetch path. */
    mimeType?: string;
    /** Existing saved-document id — passed through to the save payload for update. */
    docId?: string;
    /** Server-assigned generation-lineage id — passed through to the save payload
     *  so a new version groups with its siblings (mirrors the Plate editor's
     *  persist path in ReportEditorRoute). */
    genId?: string;
    /** Save context; absent for the read-only viewer (Save disabled). */
    assesseeOrgId?: string;
    investorOrgId?: string;
    createdBy?: string;
    documentType?: string;
    name?: string;
    description?: string;
    /** S3 storage ref from a previously saved document — enables version continuity. */
    storage?: SheetStorageRef;
    /** The opened document's status ('DRAFT' | 'READY') — a draft-preferred open
     *  (getDocument by genId) can hand the drawer a live autosave draft, which
     *  Save & Close must still commit even when the session itself made no edits. */
    status?: string;
    /** Called with the saved document after a successful save, plus whether
     *  the caller should treat this save as closing the drawer —
     *  `true` for Save & Close (the default), `false` for the Ctrl/Cmd+S
     *  shortcut, which saves without closing. */
    onSaved?: (doc: any, meta?: { closing: boolean }) => void;
    /** Called with the saved document after a name/description edit (the header's
     *  "Edit name & description" dialog) succeeds. Unlike `onSaved`, this does not
     *  imply the drawer should close — the caller only needs to refresh its own
     *  list view; the drawer already reflects the new name via `currentName`. */
    onRenamed?: (doc: any) => void;
    /** The drawer's own close callback — drives the close/flush/retry
     *  orchestration (see useSheetCloseFlow). Optional so callers that only
     *  need the fetch/save/autosave surface (e.g. this hook's own unit
     *  tests) aren't forced to stub it. */
    onClose?: () => void;
    /** A device import opens the drawer before its own eager READY save
     *  resolves a docId — this promise (Reports' own handleImportFile)
     *  resolves to that save's {docId, genId, storage} on success, or null
     *  once it's clear none is coming. runAutosave/flushPendingAutosave
     *  await it before a DRAFT persist, so a background save never mints a
     *  second lineage — or a second S3 storage doc — while the eager save is
     *  still resolving the first one. Absent for every other open path (an
     *  existing report, a template result, a fetch-URL open), where there's
     *  nothing to wait for. */
    pendingDocIdPromise?: Promise<{ docId: string; genId?: string; storage?: SheetStorageRef } | null>;
    /** Set ONLY by openBlankSheet ("New Spreadsheet") — the approved rule
     *  is that a blank new sheet enters Workspace ONLY via
     *  an explicit Save & Close, never via close/unmount, even once typed
     *  into. A template result or a new version under an existing lineage
     *  has no persisted row yet either, but must still persist on close like
     *  any other dirty sheet — so this, not "no docId yet", is what the
     *  close-flush/unmount no-op below keys off. */
    isBlankNewSheet?: boolean;
    /** True while a device import's file is still being parsed in the
     *  background (Reports' own handleImportFile) — useResolveSheets has
     *  nothing in memory yet during this window and settles to its blank
     *  EMPTY_SHEET_FALLBACK (`loading: false`), so every persist path must
     *  also check this, not just `loading`, or a Cmd/Ctrl+S (or a Save/
     *  Rename click — neither is disabled during this window) would save
     *  that blank fallback as real content. */
    preparing?: boolean;
    /** The file's original bytes — set by a caller that already holds them (a
     *  device import, Ask AICA's fetched blob) ONLY when the workbook is in the
     *  read-only band, so Download / Save / Add store them instead of re-serialising. */
    originalBytes?: ArrayBuffer;
}

const noop = (): void => {};

export const EMPTY_SHEET: FortuneSheet = { name: 'Sheet1', id: '0', order: 0, status: 1, celldata: [], row: 50, column: 26 };

/**
 * Pick the sheet the user is actually viewing. Fortune-Sheet tracks the active
 * tab via `ctx.currentSheetId` (exposed as `getSheet().id`), NOT the per-sheet
 * `status` field — `status` is set once at load and never updated on tab switch,
 * so `status === 1` always resolves to sheet[0]. Match the live active id first,
 * then fall back to status, then sheet[0]. Exported for unit tests.
 */
export const pickActiveSheet = (sheets: FortuneSheet[], activeId?: string | null): FortuneSheet =>
    sheets.find((s) => String(s.id) === String(activeId)) ?? sheets.find((s) => Number(s.status) === 1) ?? sheets[0];

// Row/col/sheet structural changes (@fortune-sheet/core's patchToOp emits
// these with an empty path, identified by `op` alone) always carry real content.
const STRUCTURAL_OPS: ReadonlySet<Op['op']> = new Set(['insertRowCol', 'deleteRowCol', 'addSheet', 'deleteSheet']);
// Per-sheet path roots that hold real cell/sheet content or formatting, as
// opposed to Fortune-Sheet's activation/selection/scroll bookkeeping (tab
// switch, clicking around, scrolling).
const CONTENT_OP_PATH_ROOTS = new Set(['data', 'celldata', 'config', 'calcChain', 'row', 'column']);

/**
 * True when `op` represents content/format a save must capture. Verified
 * against @fortune-sheet/core's source (patchToOp, the Sheet/Context types,
 * and the sheet-tab-click handler):
 * - A real edit / paste / border / merge / column-resize mutates
 *   `luckysheetfile[i].<data|celldata|config|calcChain|row|column>` — patchToOp
 *   slices that down to `{ id: <sheetId>, path: ['data', ...], ... }`.
 * - Row/col insert-delete and add/delete-sheet are synthesised with an empty
 *   path but a literal `op` (`insertRowCol`/`deleteRowCol`/`addSheet`/`deleteSheet`).
 * - Tab switch, cell-click/selection, and scrolling only ever mutate TOP-LEVEL
 *   context fields (`currentSheetId`, `zoomRatio`, `sheetScrollRecord`,
 *   `luckysheet_select_save`, `luckysheet_selection_range`, `scrollLeft/Top`,
 *   `status`, …) — patchToOp never sets `id` for those, even when a field name
 *   collides with a per-sheet one (e.g. the tab-switch `zoomRatio` mirror), so
 *   requiring `id` here is what excludes them, not the specific path names.
 * Exported for reuse.
 */
export const isContentOp = (op: Op): boolean => {
    if (STRUCTURAL_OPS.has(op.op)) return true;
    if (!op.id) return false;
    return CONTENT_OP_PATH_ROOTS.has(String(op.path[0]));
};

/** True when at least one op in the batch is content (see isContentOp). */
export const hasContentOp = (ops: Op[]): boolean => ops.some(isContentOp);

// Stable reference — passed to useResolveSheets as an effect dependency, so it
// must not be reallocated per render. The editable drawer wants a usable blank
// grid when a fetched file parses to nothing.
const EMPTY_SHEET_FALLBACK: FortuneSheet[] = [EMPTY_SHEET];

// File-scoped so two different legacy-.xls files opened in sequence (without
// the first's toast being dismissed) each get their own toast.info() call —
// react-toastify's duplicate-id guard otherwise silently drops the second
// call while a toast with the SAME id is already showing.
export const legacyXlsToastId = (fileName: string): string => `sheet-legacy-xls:${fileName}`;

/**
 * Owns the sheet-editor drawer's non-UI concerns: resolve sheets (in-memory or
 * fetched), track loading/error, and expose download + save handlers. Keeps
 * SheetEditorDrawer focused on composition/JSX.
 */
export const useSheetEditorDrawerLogic = ({
    open,
    fileName,
    sheets: inMemorySheets,
    fileUrl,
    mimeType,
    docId,
    genId,
    assesseeOrgId,
    investorOrgId,
    createdBy,
    documentType,
    name,
    description,
    storage: initialStorage,
    status: initialStatus,
    onSaved,
    onRenamed,
    onClose = noop,
    pendingDocIdPromise,
    isBlankNewSheet,
    preparing,
    originalBytes: originalBytesProp,
}: UseSheetEditorDrawerLogicArgs) => {
    // Save is only available with a persistence context (Reports) — the read-only
    // view contexts (Ask AICA, Data Room, Magic Upload, FileViewerDrawer) pass no
    // assesseeOrgId. The single source of truth for `canSave`; SheetEditorDrawer
    // consumes it from this hook's return value rather than re-deriving it.
    const canSave = Boolean(assesseeOrgId);

    // Resolve sheets (in-memory or fetch+parse) — shared with the read-only
    // SheetView via this hook, keeping the fetch/parse flow in one place.
    const {
        sheets,
        loading,
        loadingStage,
        error,
        tooLarge,
        largeNotice,
        originalBytes: fetchedOriginalBytes,
        legacyXlsRoute,
    } = useResolveSheets({
        inMemorySheets,
        fileUrl,
        mimeType,
        fileName,
        open,
        emptyFallback: EMPTY_SHEET_FALLBACK,
        context: canSave ? 'editor' : 'view',
    });
    // False while `preparing` (an import's parse still running in the
    // background), still `loading`/`error`/`tooLarge` — every persist path
    // (handleSave/handleRename/runAutosave/flushPendingAutosave/the unmount
    // safety net) must refuse to run while this is false, since
    // useResolveSheets otherwise reports `loading: false` with only its
    // blank EMPTY_SHEET_FALLBACK in hand.
    const sheetIsLoaded = !preparing && !loading && !error && !tooLarge;
    // A ref (not just the closure value) so the []-deps unmount safety net
    // below can read the LATEST value.
    const sheetIsLoadedRef = useRef(sheetIsLoaded);
    sheetIsLoadedRef.current = sheetIsLoaded;
    const [saving, setSaving] = useState(false);
    const [savingPhase, setSavingPhase] = useState<SavingPhase>('idle');
    const workbookRef = useRef<WorkbookInstance>(null);

    // Track the S3 storage ref across saves within this session. Seeded from
    // the prop (opening an already-S3-backed doc), then latched after each save
    // — every save is an in-place PUT on the SAME S3 key, so this only ever
    // gains a fresh version number (same numeric docId) after the first save.
    // A REF like liveDocIdRef/liveGenIdRef, not state: it's read inside async
    // save closures, and a stale-closure read of undefined would re-mint a
    // second mnemosyne lineage for the same document.
    const storageRef = useRef<SheetStorageRef | undefined>(initialStorage);

    // True while the lineage's tip is an uncommitted draft this session must
    // still promote — seeded from the opened doc's status (a draft-preferred
    // open hands us a live draft), set by each DRAFT autosave, cleared by a
    // successful READY save. Lets Save & Close distinguish "nothing at all to
    // commit" (close silently) from "clean workbook but a pending draft".
    const hasUncommittedDraftRef = useRef(initialStatus === 'DRAFT');

    // True once `onSaved` has fired for this drawer's CURRENT lineage this
    // session. Starts true when opened with an EXISTING docId (Reports
    // already lists that row, nothing to refresh); false for a fresh
    // import/blank sheet, whose docId only arrives later via the eager
    // import save or the first commit — so ITS first successful "save",
    // even one that takes the "nothing to commit" fast path below because
    // the eager save already did the actual persisting, still tells Reports
    // to fetch it at least once. See handleSave's own notifySaved.
    const hasNotifiedSavedRef = useRef(Boolean(docId));

    // The live doc/lineage identity — seeded from props, then latched after
    // the first save mints them (a fresh import/blank sheet has neither yet).
    // Mirrors ReportEditorRoute's liveDocIdRef so every save after the first
    // (autosave or explicit) updates the SAME row instead of forking a new one.
    const liveDocIdRef = useRef<string | undefined>(docId);
    const liveGenIdRef = useRef<string | undefined>(genId);

    // A Reports import opens the drawer immediately (no id yet); its own eager
    // READY save resolves later and only updates the PARENT's state, not this
    // ref — so `docId`/`genId`/`storage` can each arrive as a prop well after
    // mount. Latch them in as soon as they do, so every gate below that reads
    // `liveDocIdRef.current` (the flush, the safety net, the first autosave)
    // sees the imported row instead of treating it as brand-new and minting a
    // second, orphan lineage.
    useEffect(() => {
        if (docId && !liveDocIdRef.current) liveDocIdRef.current = docId;
        if (genId && !liveGenIdRef.current) liveGenIdRef.current = genId;
        if (initialStorage && !storageRef.current) storageRef.current = initialStorage;
    }, [docId, genId, initialStorage]);

    // A ref (not the raw prop) so runAutosave/flushPendingAutosave always
    // await the LATEST render's promise — Reports hands this in fresh on
    // every re-render but the underlying promise identity is stable per
    // import.
    const pendingDocIdPromiseRef = useRef(pendingDocIdPromise);
    pendingDocIdPromiseRef.current = pendingDocIdPromise;
    // A ref (not the raw prop) so the []-deps unmount safety net below can
    // read it — see isBlankNewSheet's own doc comment for what it gates.
    const isBlankNewSheetRef = useRef(isBlankNewSheet);
    isBlankNewSheetRef.current = isBlankNewSheet;
    // Awaited at the top of runAutosave/flushPendingAutosave, before any
    // DRAFT persist — a no-op once liveDocIdRef is already known, or when
    // this open didn't come from a device import (pendingDocIdPromise
    // absent). Otherwise waits for the import's own eager READY save to
    // either mint the lineage (latched here directly from the resolved
    // value, not the docId/genId PROP effect above, which may not have
    // re-rendered yet) or resolve null (too-large / failed), so a DRAFT
    // autosave/flush never races it into minting a second lineage.
    const awaitPendingDocId = async (): Promise<void> => {
        if (liveDocIdRef.current || !pendingDocIdPromiseRef.current) return;
        const resolved = await pendingDocIdPromiseRef.current;
        if (resolved?.docId && !liveDocIdRef.current) liveDocIdRef.current = resolved.docId;
        if (resolved?.genId && !liveGenIdRef.current) liveGenIdRef.current = resolved.genId;
        // Latched here directly, not left to the initialStorage prop effect
        // above — that only runs on the NEXT render, so a persist that
        // continues right after this await (in the SAME tick) would
        // otherwise still see storageRef.current unset and mint a second S3
        // storage doc instead of continuing the eager save's own one.
        if (resolved?.storage && !storageRef.current) storageRef.current = resolved.storage;
    };

    // Bounds awaitPendingDocId to AWAIT_PENDING_DOC_ID_CLOSE_TIMEOUT_MS — a
    // hung eager-import upload must not wedge Save/Rename/autosave/close
    // forever. Shared by every caller of awaitPendingDocId (previously only
    // the close path itself was bounded, inline).
    const awaitPendingDocIdBounded = (): Promise<boolean> =>
        new Promise<boolean>((resolve) => {
            const timeoutId = window.setTimeout(() => resolve(false), AWAIT_PENDING_DOC_ID_CLOSE_TIMEOUT_MS);
            const settle = (): void => {
                window.clearTimeout(timeoutId);
                resolve(true);
            };
            // A rejecting pendingDocIdPromise (the caller's own eager save
            // threw outright) is treated exactly like it resolved null —
            // awaitPendingDocId() already no-ops on a null resolution (there
            // is nothing to latch either way) — so this settles the SAME
            // way, promptly, instead of waiting out the full timeout and
            // leaving the rejection unhandled.
            awaitPendingDocId().then(settle, settle);
        });

    // Single in-flight guard shared by autosave, Save & Close, and Rename — a
    // ref (not just the `saving` state) so a debounced autosave firing between
    // renders sees the latest value synchronously, with no stale-closure risk.
    const savingRef = useRef(false);
    // True from the last workbook edit until a save (autosave or explicit)
    // succeeds. Cleared ONLY on success — a failed autosave must retry.
    const dirtyRef = useRef(false);
    // Bumped on every edit (markWorkbookDirty) — runAutosave snapshots this
    // before its persist
    // call and only clears `dirtyRef` if it's unchanged on success, so an
    // edit that lands mid-upload (after the snapshot persistSheet actually
    // read) is never silently marked as saved.
    const editGenerationRef = useRef(0);
    // Set only by an explicit "Close without saving" click (the save-failed dialog)
    // — both the flush and the unmount safety net skip saving once set, so a
    // discarded edit is never silently resurrected by a late autosave.
    const discardRef = useRef(false);
    // Render-side mirror of dirtyRef for the header's saved/unsaved indicator
    // and the close guard. Only written on a transition (never per keystroke)
    // so a typing burst doesn't re-render the drawer header on every op.
    const [hasUnsavedEdits, setHasUnsavedEdits] = useState(false);
    const markClean = useCallback(() => {
        dirtyRef.current = false;
        setHasUnsavedEdits(false);
    }, []);
    // Sticky "differs from the original file" for the view-context Workspace
    // guard — markClean never touches it. The guard's content-edit counter is
    // editGenerationRef above.
    const editedSinceOpenRef = useRef(false);
    const [hasEditedSinceOpen, setHasEditedSinceOpen] = useState(false);
    const getEditVersion = useCallback(() => editGenerationRef.current, []);
    const autosaveTimerRef = useRef<number | null>(null);
    // Stable indirection so the debounce timer's setTimeout callback always
    // invokes the LATEST autosave closure (fresh saving/dirty/meta state),
    // never one captured stale at schedule time.
    const runAutosaveRef = useRef<() => void>(() => {});

    // Background autosave status — separate from `saving`/`savingPhase` (which
    // the Save & Close / Rename button reads) so a debounced autosave tick
    // never disables or relabels that button. Only runAutosave writes this.
    const [autosaveStatus, setAutosaveStatus] = useState<AutosaveStatus>('idle');
    const autosaveStatusDecayRef = useRef<number | null>(null);

    // The currently-running save/rename/autosave operation (whichever holds
    // savingRef) — lets an explicit Save & Close or Rename that arrives while
    // an autosave tick is mid-flight AWAIT it and then proceed, instead of
    // silently no-oping and leaving the user's click unanswered.
    const inFlightSaveRef = useRef<Promise<any> | null>(null);

    // Own the current name/description here (seeded from props) rather than
    // reading the props directly in handleSave — a rename updates these in
    // place so a later plain Save persists the edited name, not the stale one
    // the drawer was opened with.
    const [currentName, setCurrentName] = useState(name);
    const [currentDescription, setCurrentDescription] = useState(description);
    // The unmount safety-net cleanup below is a []-deps
    // effect, so its closure is captured once at mount — mirror the latest
    // name/description into refs (same "always current" pattern as
    // useSheetKeyboardShortcuts.ts's latestRef) so a rename-then-edit-then-
    // unmount sends the CURRENT metadata, not whatever was current at mount.
    const currentNameRef = useRef(currentName);
    currentNameRef.current = currentName;
    const currentDescriptionRef = useRef(currentDescription);
    currentDescriptionRef.current = currentDescription;
    // Same "always current" reasoning — a drawer instance reused for a
    // different assessee/org (assesseeOrgId changing without a remount)
    // must have the safety net gate on the CURRENT value, not whatever was
    // current (possibly absent — a read-only viewer at mount) when this
    // []-deps effect's closure was captured.
    const assesseeOrgIdRef = useRef(assesseeOrgId);
    assesseeOrgIdRef.current = assesseeOrgId;

    // True while the version-history side pane is open — read via a ref (not
    // the closure) so markWorkbookDirty always sees the current value without
    // being re-created on every history toggle.
    const historyOpenRef = useRef(false);
    // A historical version currently shown read-only in place of the live
    // workbook. Kept entirely separate from `sheets`/`restoredSheets` — preview
    // content must never reach persistSheet or mark the workbook dirty.
    const [previewSheets, setPreviewSheets] = useState<FortuneSheet[] | null>(null);
    const [previewEntryId, setPreviewEntryId] = useState<string | null>(null);
    // Sheets from a successful restore — becomes the new "live" editable
    // content (the original `sheets` fetch is never mutated). Forces a
    // Workbook remount even when previewEntryId doesn't change (e.g. a direct
    // 3-dot restore with no prior preview).
    const [restoredSheets, setRestoredSheets] = useState<FortuneSheet[] | null>(null);
    const [restoreVersion, setRestoreVersion] = useState(0);
    // A snapshot of the LIVE (session-edited) sheets, taken the moment the
    // first non-latest preview swaps the mounted content out — Fortune only
    // reads `data` at init, so closing the pane (or picking another preview)
    // would otherwise remount from the as-loaded `sheets`, silently dropping
    // whatever was edited this session. `onDrawerClose` restores from this via
    // `restoredSheets`; `handleRestoreVersion` clears it first so a genuine
    // restore can't be clobbered by a stale snapshot taken before it.
    const liveSnapshotRef = useRef<FortuneSheet[] | null>(null);
    const historyEntriesRef = useRef<HistoryEntry[]>([]);
    const closeHistoryRef = useRef<(() => void) | null>(null);
    // Cache of the CURRENTLY-viewed history version (sheets + its storage ref),
    // keyed by entry id. Bounded to one entry — selecting another version
    // evicts the old one, so browsing N versions of a large workbook never
    // holds N parsed copies in memory. Re-clicking the same row is still
    // instant. Cleared when the history pane closes (incl. after a restore).
    const versionCacheRef = useRef<Map<string, { sheets: FortuneSheet[]; storage?: SheetStorageRef }>>(new Map());

    // Set for the duration of each hydration updateSheet() call — see
    // useLazySheets' suppressDirtyRef and markWorkbookDirty below.
    const suppressDirtyRef = useRef(false);

    // Large multi-tab workbooks mount with only the active sheet hydrated —
    // stubs fill in right after first paint (see useLazySheets for the why).
    // A restored version takes over as the "live" sheets going forward.
    const effectiveSheets = restoredSheets ?? sheets;

    // Files above MAX_EDITABLE_CELLS filled cells open read-only: re-serialising
    // them (exceljs) would cross the tab's memory budget, so NO read-only path
    // below ever builds an xlsx. Decided on the sheets the drawer actually
    // mounts, once per mount — allowEdit is never flipped live (that re-inits
    // Fortune); a session that grows past the cap by pasting stays editable.
    const filledCells = useMemo(() => countFilledCells(effectiveSheets ?? []), [effectiveSheets]);
    const sizeReadOnly = sheetIsLoaded && isReadOnlySize(filledCells);
    const sizeReadOnlyRef = useRef(sizeReadOnly);
    sizeReadOnlyRef.current = sizeReadOnly;
    const filledCellsRef = useRef(filledCells);
    filledCellsRef.current = filledCells;
    const originalBytes = originalBytesProp ?? fetchedOriginalBytes ?? undefined;
    const originalBytesRef = useRef(originalBytes);
    originalBytesRef.current = originalBytes;
    const originalIsXlsx = Boolean(originalBytes) && isXlsxBuffer(originalBytes as ArrayBuffer);
    // Read-only saves re-point at stored bytes (storage ref) or upload the original .xlsx.
    const canPersistReadOnly = Boolean(initialStorage ?? storageRef.current) || originalIsXlsx;
    // After a restore the opened file's bytes/URL are the PRE-restore version;
    // the version on screen lives at storageRef (re-pointed by the restore).
    const restored = restoredSheets !== null;
    // A band .xlsx already in memory: the host's Add stores these bytes instead of
    // re-fetching and re-parsing the file (a second copy of a 2M-cell workbook).
    const readOnlyAddBand: ReadOnlyAddBand | undefined =
        sizeReadOnly && originalIsXlsx && !restored ? { rawBytes: originalBytes as ArrayBuffer, filledCells } : undefined;
    const canDownloadOriginal = restored ? Boolean(storageRef.current) : Boolean(originalBytes || fileUrl);

    const readOnlyNotifiedRef = useRef(false);
    useEffect(() => {
        if (!open || !sizeReadOnly) {
            readOnlyNotifiedRef.current = false;
            return;
        }
        if (readOnlyNotifiedRef.current) return;
        readOnlyNotifiedRef.current = true;
        logSheetLoadFailure({
            stage: 'read_only',
            context: canSave ? 'editor' : 'view',
            fileBytes: originalBytes?.byteLength ?? 0,
            sheetCount: effectiveSheets?.length,
            cellCount: filledCells,
            elapsedMs: 0,
            message: `Opened read-only: filled ${filledCells} (max editable ${MAX_EDITABLE_CELLS})`,
            ext: fileName.split('.').pop()?.toLowerCase() ?? '',
        });
        toast.info(readOnlyNoticeMessage(filledCells), { toastId: 'sheet-read-only' });
    }, [open, sizeReadOnly, canSave, originalBytes, effectiveSheets, filledCells, fileName]);

    // One-time info toast for a legacy .xls: values/number formats survive the
    // SheetJS parse, but fonts/borders/alignment don't (no library in the repo
    // reads BIFF styles). Gated on the actual parse route (isLegacyXlsRoute)
    // on the fetch path, so a ZIP-content file merely named .xls — routed to
    // exceljs, full style fidelity — no longer mis-fires. The extension
    // heuristic is the fallback ONLY for the in-memory path (Reports, Ask
    // AICA import hosts) — those never flow through useResolveSheets's fetch
    // path, so `legacyXlsRoute` stays permanently null for them and there's
    // no settled verdict to wait for. On the fetch path, `null` means "not
    // resolved for this file yet" (never the extension), so a still-pending
    // fetch (or a just-switched-to file's stale prior-render value — see
    // useResolveSheets) never gets misclassified off a filename guess.
    //
    // Gated on `sheetIsLoaded` (not just `error`) so it only fires once the
    // parse has actually settled, and keyed by `fileName` (not a plain
    // fired-once boolean) so it survives a close/reopen of the SAME file for
    // callers that keep this hook mounted across opens (e.g. Reports), while
    // still re-firing for a genuinely different file.
    const isLegacyXls = legacyXlsRoute ?? (Boolean(inMemorySheets) && fileName.split('.').pop()?.toLowerCase() === 'xls');
    const legacyXlsNotifiedForFileRef = useRef<string | null>(null);
    useEffect(() => {
        if (!open || !sheetIsLoaded || !isLegacyXls) return;
        if (legacyXlsNotifiedForFileRef.current === fileName) return;
        // Dismiss the previous file's notice before marking the new one
        // notified — otherwise two different legacy files opened within the
        // first's auto-close window would stack two identical toasts.
        const previouslyNotifiedFile = legacyXlsNotifiedForFileRef.current;
        if (previouslyNotifiedFile) toast.dismiss(legacyXlsToastId(previouslyNotifiedFile));
        legacyXlsNotifiedForFileRef.current = fileName;
        toast.info(
            "Legacy .xls files preview with values intact; supported number formats are preserved but unsupported ones may show as General. Original formatting (fonts, borders, colours) isn't preserved. The original file can still be downloaded unchanged.",
            { toastId: legacyXlsToastId(fileName) },
        );
    }, [open, sheetIsLoaded, isLegacyXls, fileName]);
    // Whichever content the <Workbook> currently mounts — a historical preview
    // (read-only, in place) or the live/editable sheets. Routed through the
    // SAME lazy-hydration machinery either way, so a large previewed version
    // mounts just as fast as the live workbook (a preview used to bypass
    // useLazySheets and mount every sheet eagerly).
    const displaySheets = previewSheets ?? effectiveSheets;
    const { workbookSheets, getFullSheets, hydratePending } = useLazySheets({
        sheets: displaySheets,
        workbookRef,
        suppressDirtyRef,
    });

    // Kick off stub hydration once the Workbook is mounted. Effects run after
    // the commit that mounts the child Workbook, so workbookRef is populated.
    useEffect(() => {
        if (!loading && !error && workbookSheets?.length) hydratePending();
    }, [loading, error, workbookSheets, hydratePending]);

    /** Latest sheet state: prefer the live workbook, fall back to resolved sheets.
     *  getFullSheets swaps never-activated stubs back for their full originals —
     *  without it, saving/downloading before touching every tab would wipe them. */
    const currentSheets = (): FortuneSheet[] => {
        const live = workbookRef.current?.getAllSheets();
        return live && live.length ? getFullSheets(live) : effectiveSheets ?? [];
    };

    // `tooLarge.bytes`: the too-large screen's Download saves the bytes it already fetched.
    const handleDownloadOriginal = async (): Promise<void> => {
        if (!restored) {
            downloadOriginalBytes(originalBytesRef.current ?? tooLarge?.bytes, fileUrl, fileName);
            return;
        }
        if (!storageRef.current) return;
        try {
            const response = await fetch(await getSheetDownloadUrl(storageRef.current));
            if (!response.ok) throw new Error(`restored version download failed: HTTP ${response.status}`);
            downloadBlobFromUrl(new Blob([await response.arrayBuffer()]), fileName);
        } catch (err) {
            console.error('[useSheetEditorDrawerLogic] restored version download failed', err);
            toast.error('Could not download this file. Please try again.');
        }
    };

    const handleDownloadXlsx = async () => {
        if (sizeReadOnlyRef.current) {
            await handleDownloadOriginal();
            return;
        }
        const data = currentSheets();
        if (!data.length) return;
        try {
            await downloadAsXlsx(data, fileName);
            toast.success('Downloaded as .xlsx');
        } catch (err) {
            console.error('[useSheetEditorDrawerLogic] xlsx download failed', err);
            toast.error('Could not download this file. Please try again.');
        }
    };

    const handleDownloadCsv = () => {
        if (sizeReadOnlyRef.current) {
            handleDownloadOriginal();
            return;
        }
        const data = currentSheets();
        if (!data.length) return;
        // Resolve against the live workbook's active sheet id (see pickActiveSheet);
        // getFullSheets keeps stub-safety by resolving that id in currentSheets().
        const active = pickActiveSheet(data, workbookRef.current?.getSheet()?.id);
        downloadAsCsv(active, fileName);
        toast.success(`Downloaded "${active.name}" as .csv`);
    };

    /**
     * Single save path shared by autosave (DRAFT), Save & Close, and Rename
     * (both READY). Uploads the current sheets (S3, or inline behind the
     * kill-switch) and persists the aica document row, addressed by whatever
     * doc/lineage identity is already known (a fresh doc omits both and lets
     * the backend mint them). `metaFields` carries name/description exactly as
     * the caller wants them spread — handleSave/autosave only send them when
     * truthy, Rename always sends both (including clearing to '') — so this
     * function never guesses at that policy itself.
     *
     * No inline fallback on S3 failure: a workbook large enough to need S3
     * would fail the same way against Mongo's 16 MB cap, just with a more
     * confusing error — so a failed upload fails the save outright instead of
     * silently retrying against a path that can't actually hold it.
     *
     * `silent` (background autosave) never touches `savingPhase` — that state
     * drives the Save & Close button's label, which must read its idle "Save
     * and Close" label throughout a background tick, not "Uploading…"/"Saving…".
     */
    const persistSheet = useCallback(
        async (
            status: 'DRAFT' | 'READY',
            metaFields: Record<string, unknown> = {},
            options: {
                /** Restore: omit `id` so the backend mints a new row in the SAME lineage (genId is kept). */
                fork?: boolean;
                isRestored?: boolean;
                /** Restore: the previewed version's sheets — already celldata-shaped
                 *  (read back from storage/history), never the live workbook's. */
                sheetsOverride?: FortuneSheet[];
                /** Restore: the previewed version's own storage ref. Its bytes already
                 *  exist as an immutable S3 object version, so the save just points the
                 *  new document row at it — no re-serialize, no re-upload. */
                storageOverride?: SheetStorageRef;
                /** Background autosave: never sets savingPhase (see the doc comment above). */
                silent?: boolean;
                /** A runtime-shape (2D `data`) snapshot taken synchronously
                 *  BEFORE the workbook unmounts (the layout-effect safety net) —
                 *  used in place of `currentSheets()` when the live ref may
                 *  already be gone. Distinct from `sheetsOverride`, which is
                 *  celldata-shaped and restore-only. */
                liveSheets?: FortuneSheet[];
            } = {},
        ): Promise<any> => {
            const { fork, isRestored, sheetsOverride, storageOverride, silent, liveSheets } = options;
            // Hard rule: a DRAFT persist must never fall back to the
            // as-loaded `effectiveSheets` inside currentSheets() — that IS the
            // pre-existing close-flush data-loss bug. With no live
            // workbook and no pre-unmount snapshot, there is nothing safe to save.
            if (status === 'DRAFT' && !sheetsOverride && !liveSheets && !workbookRef.current) {
                throw new Error('[useSheetEditorDrawerLogic] DRAFT persist attempted with no live workbook and no snapshot');
            }
            const readOnly = sizeReadOnlyRef.current;
            // A read-only-band workbook is never serialised: no inline save,
            // and no `uploadSheetVersion({ sheets })` below.
            if (readOnly && !isSheetS3StorageEnabled()) throw new SheetReadOnlyAddError(filledCellsRef.current);
            const getSheetsToSave = (): FortuneSheet[] => sheetsOverride ?? liveSheets ?? currentSheets();
            // Callers (handleSave/handleRename/runAutosave/handleRestoreVersion)
            // already gate on assesseeOrgId being present before ever calling persistSheet.
            const identityFields = {
                assesseeOrgId: assesseeOrgId as string,
                ...(!fork && liveDocIdRef.current ? { id: liveDocIdRef.current } : {}),
                ...(liveGenIdRef.current ? { genId: liveGenIdRef.current } : {}),
                ...(investorOrgId ? { investorOrgId } : {}),
                ...(createdBy ? { createdBy } : {}),
                ...(documentType ? { documentType } : {}),
                ...(isRestored ? { isRestored: true } : {}),
                ...metaFields,
            };

            let savedDoc: any;
            if (isSheetS3StorageEnabled()) {
                let ref = storageOverride ?? (readOnly ? storageRef.current : undefined);
                if (!ref && readOnly) {
                    // No stored bytes yet (a fresh import): upload the original .xlsx as-is.
                    const rawBytes = originalBytesRef.current;
                    if (!rawBytes || !isXlsxBuffer(rawBytes)) throw new SheetReadOnlyAddError(filledCellsRef.current);
                    if (!silent) setSavingPhase('uploading');
                    ref = await uploadSheetVersion({
                        rawBytes,
                        assesseeOrgId: assesseeOrgId as string,
                        investorOrgId,
                        isDraft: status === 'DRAFT',
                    });
                }
                if (!ref) {
                    if (!silent) setSavingPhase('uploading');
                    // Live workbook sheets are RUNTIME shape (2D `data`) — serialize via
                    // the combined worker job (toCelldataSheet + buildXlsxBuffer in one
                    // round trip). A restore's sheetsOverride is already celldata, so it
                    // skips that conversion (runtimeSheets: false).
                    ref = await uploadSheetVersion({
                        sheets: getSheetsToSave(),
                        assesseeOrgId: assesseeOrgId as string,
                        investorOrgId,
                        existing: storageRef.current,
                        runtimeSheets: !sheetsOverride,
                        // Autosave re-points the lineage's live draft version in place
                        // (same versionNumber); Save & Close finalizes it — one history
                        // version per commit, mirroring the aica draft row it sits under.
                        isDraft: status === 'DRAFT',
                    });
                }
                storageRef.current = ref;

                if (!silent) setSavingPhase('saving');
                savedDoc = await saveDocument({ ...identityFields, editor: 'sheet', formatType: 'XLSX', status, storage: ref });
            } else {
                // Kill-switch path only (SHEET_S3_STORAGE_ENABLED === false). Small
                // sheets are cheap to walk on the main thread.
                const data = sheetsOverride ?? getSheetsToSave().map(toCelldataSheet);
                savedDoc = await saveDocument({
                    ...identityFields,
                    editor: 'sheet',
                    formatType: 'XLSX',
                    status,
                    content: { version: 1, editor: 'sheet', value: data },
                });
            }

            const savedId = savedDoc?.id ?? savedDoc?._id;
            if (savedId) liveDocIdRef.current = savedId;
            if (savedDoc?.genId) liveGenIdRef.current = savedDoc.genId;
            // A DRAFT save leaves the lineage tip pending promotion; a READY save
            // (commit / rename / restore) promotes it — nothing left to commit.
            hasUncommittedDraftRef.current = status === 'DRAFT';
            return savedDoc;
        },
        [assesseeOrgId, investorOrgId, createdBy, documentType],
    );
    // The unmount safety net below is a []-deps layout effect, so its
    // cleanup closure is captured once at mount — mirrors currentNameRef/
    // currentDescriptionRef above (same "always current" pattern as
    // useSheetKeyboardShortcuts.ts's latestRef) so a drawer instance reused
    // for a different document (assesseeOrgId/investorOrgId/createdBy/
    // documentType changing without a remount) saves under the CURRENT
    // identity, not whatever was current at mount.
    const persistSheetRef = useRef(persistSheet);
    persistSheetRef.current = persistSheet;

    /** Returns true only on a successful save. `opts.source` defaults to
     *  'saveAndClose' (the header button, which closes the drawer itself on a
     *  `true` return) — the Ctrl/Cmd+S shortcut passes 'shortcut' so `onSaved`
     *  can tell Reports not to close the drawer for it. */
    const handleSave = useCallback(
        async (opts?: { source?: 'saveAndClose' | 'shortcut' }): Promise<boolean> => {
            // Marks this lineage notified, then fires onSaved — shared by
            // every return point below so a fresh import's eager save gets
            // its ONE Reports refresh no matter which path first observes
            // "there's nothing left for handleSave itself to persist".
            const notifySaved = (doc: unknown): void => {
                hasNotifiedSavedRef.current = true;
                onSaved?.(doc, { closing: (opts?.source ?? 'saveAndClose') === 'saveAndClose' });
            };
            if (!assesseeOrgId) return false; // no save context (read-only viewer)
            // An autosave (or another save/rename) is mid-flight — wait for it
            // instead of silently no-oping this click. Re-checked in a loop: two
            // clicks released by the same autosave must not both claim savingRef.
            while (savingRef.current && inFlightSaveRef.current) await inFlightSaveRef.current;
            if (!sheetIsLoaded) return false; // still preparing/loading/errored/too-large — nothing real to save yet
            if (sizeReadOnly && !canPersistReadOnly) {
                toast.info(readOnlySaveMessage(filledCells));
                return false;
            }
            // Ctrl/Cmd+S on a read-only workbook says so; Save & Close still closes.
            if (sizeReadOnly && opts?.source === 'shortcut') {
                toast.info(READ_ONLY_SHORTCUT_SAVE_NOTICE);
                return false;
            }
            // Persist the celldata init form, not the runtime 2D `data` from
            // getAllSheets() — otherwise the saved doc reopens as an empty grid.
            // A read-only-band workbook is never empty; skip the full-grid walk.
            if (!sizeReadOnly && !currentSheets().length) return false;
            // Nothing to commit: the doc already exists, nothing changed since its
            // last save, and no draft is pending promotion — close without minting a
            // pointless new version (or any network at all). A fresh never-saved
            // sheet (no liveDocId) still saves: Save & Close on it is explicit
            // create intent. A lineage Reports has never been told about (a fresh
            // import's eager save, which never calls onSaved itself) still gets
            // ONE notifySaved so its row actually shows up.
            if (!dirtyRef.current && !hasUncommittedDraftRef.current && liveDocIdRef.current) {
                if (!hasNotifiedSavedRef.current) notifySaved(undefined);
                return true;
            }
            if (autosaveTimerRef.current) {
                window.clearTimeout(autosaveTimerRef.current);
                autosaveTimerRef.current = null;
            }
            savingRef.current = true;
            setSaving(true);
            const op = (async (): Promise<boolean> => {
                try {
                    // A device import's eager READY save may still be
                    // resolving the lineage this save must land in — wait
                    // for it (or its failure), exactly like runAutosave/
                    // flushPendingAutosave, so a Save clicked before it
                    // resolves never omits `id` and forks a second lineage.
                    // savingRef is already claimed above, so single-flight
                    // still holds through this wait. Bounded: a hung eager
                    // upload must fail this save with the usual error toast
                    // (below) rather than wedging savingRef forever.
                    if (!(await awaitPendingDocIdBounded())) {
                        throw new Error(PENDING_DOC_ID_TIMEOUT_MESSAGE);
                    }
                    // Re-check "nothing to commit" (line ~584): the eager
                    // save's own docId only just latched into liveDocIdRef
                    // above — an unedited import saved/closed immediately
                    // would otherwise re-persist a READY save that already
                    // exists, unchanged, from the eager save itself. Its
                    // lineage still needs its ONE notifySaved (see above).
                    if (!dirtyRef.current && !hasUncommittedDraftRef.current && liveDocIdRef.current) {
                        if (!hasNotifiedSavedRef.current) notifySaved(undefined);
                        return true;
                    }
                    const savedGeneration = editGenerationRef.current;
                    const savedDoc = await persistSheet('READY', {
                        ...(currentName ? { name: currentName } : {}),
                        ...(currentDescription ? { description: currentDescription } : {}),
                    });
                    // Same guard as runAutosave's — an edit typed WHILE this
                    // Cmd+S/Save upload is in flight must stay dirty so it
                    // gets its own save, instead of being silently marked
                    // clean by a save that never captured it.
                    if (editGenerationRef.current === savedGeneration) markClean();
                    toast.success('Saved');
                    notifySaved(savedDoc);
                    return true;
                } catch (err) {
                    console.error('[useSheetEditorDrawerLogic] save failed', err);
                    toast.error('Failed to save');
                    return false;
                } finally {
                    setSaving(false);
                    setSavingPhase('idle');
                    savingRef.current = false;
                    inFlightSaveRef.current = null;
                }
            })();
            inFlightSaveRef.current = op;
            return op;
        },
        [
            assesseeOrgId,
            sheetIsLoaded,
            sizeReadOnly,
            canPersistReadOnly,
            filledCells,
            currentName,
            currentDescription,
            onSaved,
            persistSheet,
            markClean,
        ],
    );

    /** The exact negation of `handleSave`'s no-op gate (:436) — true when a
     *  Ctrl+S (or Save & Close) would actually do something. Exported for the
     *  keyboard shim's `saveShortcut`, which must not show "All
     *  changes saved" when there is, in fact, something to save. */
    const hasPendingChanges = useCallback(
        // A read-only workbook always routes Ctrl+S to handleSave, which explains why nothing saves.
        (): boolean => sizeReadOnlyRef.current || dirtyRef.current || hasUncommittedDraftRef.current || !liveDocIdRef.current,
        [],
    );

    // Persists a name/description edit from the header's "Edit name & description"
    // dialog — the same saveDocument call handleSave makes, carrying the CURRENT
    // sheet content through (mirrors PlateDocumentDrawer's rename, which also
    // commits the live editor content alongside the metadata change).
    const handleRename = useCallback(
        async (newName: string, newDescription: string) => {
            if (!assesseeOrgId) return;
            if (!sheetIsLoaded) return; // still preparing/loading/errored/too-large — nothing real to save yet
            if (sizeReadOnly && !canPersistReadOnly) {
                toast.info(readOnlySaveMessage(filledCells));
                return;
            }
            // A history preview is mounted read-only and formula-stripped —
            // persisting it would silently commit that stripped content as
            // the new tip (mirrors saveShortcut's own "Close version history
            // to save" gate).
            if (historyOpenRef.current) {
                toast.info('Close version history to rename.');
                return;
            }
            // An autosave (or another save/rename) is mid-flight — wait for it
            // instead of silently no-oping this click. Looped, same as handleSave:
            // the caller released alongside us may have claimed savingRef first.
            while (savingRef.current && inFlightSaveRef.current) await inFlightSaveRef.current;
            if (autosaveTimerRef.current) {
                window.clearTimeout(autosaveTimerRef.current);
                autosaveTimerRef.current = null;
            }
            // Rename commits the CURRENT content too (a full version on the S3
            // path) — so it shares the saving flag/phases with handleSave, both
            // for user feedback and to serialize against a concurrent Save.
            savingRef.current = true;
            setSaving(true);
            const op = (async () => {
                try {
                    // Same wait as handleSave — a still-resolving device
                    // import's eager save must settle the lineage first,
                    // exactly like runAutosave/flushPendingAutosave.
                    // savingRef is already claimed above, so single-flight
                    // still holds. Bounded, same as handleSave — a hung
                    // eager upload fails the rename instead of wedging it.
                    if (!(await awaitPendingDocIdBounded())) {
                        throw new Error(PENDING_DOC_ID_TIMEOUT_MESSAGE);
                    }
                    const savedGeneration = editGenerationRef.current;
                    const savedDoc = await persistSheet('READY', { name: newName, description: newDescription });
                    // Same guard as runAutosave's — an edit typed WHILE this
                    // rename's upload is in flight must stay dirty so it
                    // gets its own save.
                    if (editGenerationRef.current === savedGeneration) markClean();
                    setCurrentName(newName);
                    setCurrentDescription(newDescription);
                    onRenamed?.(savedDoc);
                } catch (err) {
                    console.error('[useSheetEditorDrawerLogic] rename failed', err);
                    toast.error('Could not rename the report. Please try again.');
                } finally {
                    setSaving(false);
                    setSavingPhase('idle');
                    savingRef.current = false;
                    inFlightSaveRef.current = null;
                }
            })();
            inFlightSaveRef.current = op;
            return op;
        },
        [assesseeOrgId, sheetIsLoaded, sizeReadOnly, canPersistReadOnly, filledCells, onRenamed, persistSheet, markClean],
    );

    /**
     * DRAFT autosave tick — fires SHEET_AUTOSAVE_DEBOUNCE_MS after the last
     * workbook edit (see markWorkbookDirty). Shares the save path and the
     * single-flight guard with handleSave/handleRename so all three can never
     * run concurrently and a failed tick simply retries on the next edit.
     *
     * Non-blocking: sets ONLY `autosaveStatus`, never `saving`/`savingPhase` —
     * those drive the Save & Close button, which must stay enabled and
     * correctly labelled through a background autosave tick.
     */
    const runAutosave = useCallback(async () => {
        if (!assesseeOrgId) return; // no save context — nothing to autosave
        if (!sheetIsLoaded) return; // still preparing/loading/errored/too-large — nothing real to save yet
        // Once discarded, no DRAFT persist may ever run again for this
        // hook instance — enforced here (the one function every autosave path
        // funnels through) rather than only at flushPendingAutosave/the unmount
        // safety net, so a stray re-arm (e.g. markWorkbookDirty firing again)
        // can never resurrect the discarded edit.
        if (discardRef.current) return;
        if (historyOpenRef.current) return; // a historical version may be mounted — never persist it as the draft
        if (savingRef.current) {
            // Another save is in flight — reschedule instead of dropping the
            // pending edit; it'll run once that save finishes.
            autosaveTimerRef.current = window.setTimeout(() => runAutosaveRef.current(), SHEET_AUTOSAVE_DEBOUNCE_MS);
            return;
        }
        if (!dirtyRef.current) return; // nothing changed since the last successful save
        if (!currentSheets().length) return;

        // savingRef/inFlightSaveRef are claimed synchronously, BEFORE any
        // await — a concurrent caller (flushPendingAutosave, Save, Rename)
        // reads them right after this debounce tick fires, with no async
        // gap in between where it could see savingRef still false and start
        // its own overlapping save. awaitPendingDocId's own wait (a device
        // import's eager READY save may still be resolving the lineage this
        // DRAFT must land in, or persistSheet's `id` field would be omitted
        // and mint a second lineage) therefore runs INSIDE this same claimed
        // op, not before it.
        savingRef.current = true;
        setAutosaveStatus('saving');
        const op = (async () => {
            try {
                // Bounded — a hung eager import upload must not wedge
                // savingRef forever; the next edit (or close) retries.
                if (!(await awaitPendingDocIdBounded())) {
                    setAutosaveStatus('error');
                    return;
                }
                // Re-check: a discard, or the edit this await itself raced,
                // could have landed while it was pending.
                if (discardRef.current || !dirtyRef.current) {
                    setAutosaveStatus('idle'); // undo the 'saving' set above — nothing was actually saved
                    return;
                }
                const savedGeneration = editGenerationRef.current;
                await persistSheet(
                    'DRAFT',
                    {
                        ...(currentName ? { name: currentName } : {}),
                        ...(currentDescription ? { description: currentDescription } : {}),
                    },
                    { silent: true },
                );
                // Only the snapshot taken above was actually persisted — a
                // later edit (bumping editGenerationRef again) must stay
                // dirty so it gets its own save.
                if (editGenerationRef.current === savedGeneration) markClean();
                setAutosaveStatus('saved');
                if (autosaveStatusDecayRef.current) window.clearTimeout(autosaveStatusDecayRef.current);
                autosaveStatusDecayRef.current = window.setTimeout(() => setAutosaveStatus('idle'), AUTOSAVE_STATUS_DECAY_MS);
            } catch (err) {
                // Never blocks editing or closes the drawer — the next edit's debounce
                // (or the flush-on-unmount below) retries with the latest content.
                console.warn('[useSheetEditorDrawerLogic] autosave failed, will retry', err);
                setAutosaveStatus('error');
            } finally {
                savingRef.current = false;
                inFlightSaveRef.current = null;
            }
        })();
        inFlightSaveRef.current = op;
        await op;
    }, [assesseeOrgId, sheetIsLoaded, currentName, currentDescription, persistSheet, markClean]);

    useEffect(() => {
        runAutosaveRef.current = runAutosave;
    }, [runAutosave]);

    /**
     * Awaited flush of a pending DRAFT autosave — called by every
     * non-cancel close path (×, backdrop, idle Esc) BEFORE `onClose`, while
     * the workbook is still mounted. A no-op (resolves `true` immediately)
     * for a view context (no save), a history preview, or a clean document. Otherwise
     * clears the pending debounce timer, waits out any already-running
     * autosave, then re-checks `dirtyRef` — that in-flight save may have
     * already captured the latest content, in which case there is nothing
     * further to save — before running the existing DRAFT save path.
     * Resolves `false` only on a genuine save failure (mirrors Save &
     * Close's "close only on a confirmed save") — including a still-
     * resolving eager import save that exceeds AWAIT_PENDING_DOC_ID_CLOSE_TIMEOUT_MS:
     * the save-failed dialog's Retry gets a fresh bounded wait.
     *
     * This shares runAutosave's own predicate (assesseeOrgId/discardRef/
     * dirtyRef/has-sheets) rather than also requiring `liveDocIdRef.current`
     * — a dirty IMPORT (or template result, or new version, including one
     * whose eager save failed or never existed at all, e.g. CSV/XLS) closed
     * WITHIN the debounce window must still persist here exactly as waiting
     * past it would (persistSheet mints a fresh lineage when `id` is absent,
     * same as any first save) — EXCEPT for a blank "New Spreadsheet", which
     * the approved rule says enters Workspace ONLY via an
     * explicit Save & Close, never via ×/backdrop/Esc, even once typed into
     * — see the isBlankNewSheet check below. An UNEDITED import (dirtyRef
     * never set) is never persisted on close either way, matching
     * origin/main — a look-only open/close must not write an invisible
     * orphan DRAFT.
     */
    const flushPendingAutosave = useCallback(async (): Promise<boolean> => {
        if (!assesseeOrgId || !sheetIsLoaded || historyOpenRef.current || discardRef.current || !dirtyRef.current) return true;
        // A blank new sheet with no persisted row never auto-saves
        // on close — isBlankNewSheetRef is true ONLY for openBlankSheet (a
        // template result or a new version under an existing lineage also
        // has no liveDocId yet, but isn't a blank sheet, so it falls through
        // and persists like any other dirty sheet). Once a docId exists
        // (e.g. an in-session Ctrl+S), this no-op no longer applies — the
        // sheet has already entered Workspace via an explicit save.
        // runAutosave itself has no equivalent gate (matches origin/main,
        // pre-existing, out of scope here) — waiting past the debounce still
        // creates a report for a typed-into blank sheet; only close/unmount
        // are fixed.
        if (!liveDocIdRef.current && isBlankNewSheetRef.current) return true;
        // liveDocIdRef may not be known yet if a device import's own eager
        // save is still resolving — wait for it (or its failure) so a DRAFT
        // persist below lands in that SAME lineage rather than minting a
        // second one; a failed/absent eager save (resolves null) still
        // proceeds to persist, exactly like runAutosave would.
        //
        // Bounded — a hung eager upload must not hang the close flow
        // forever. The "Saving…" indicator (autosaveStatus) stays visible
        // for the wait; a timeout resolves `false` here (the save-failed dialog:
        // Retry re-attempts this same bounded wait, "Close without saving"
        // discards) rather than silently forking a new lineage behind the
        // user's back while the original eager save might still land.
        setAutosaveStatus('saving');
        // Downgrades a 'saving' left over from the call above back to 'idle'
        // — a no-op once runAutosave (below) has already moved it on to its
        // own 'saved'/'error'/rescheduled-'saving' — so this never leaves the
        // indicator stuck on "Saving…" on a path that returns without it.
        const resetSavingStatus = (): void => setAutosaveStatus((s) => (s === 'saving' ? 'idle' : s));
        const pendingDocIdSettledInTime = await awaitPendingDocIdBounded();
        if (!pendingDocIdSettledInTime) {
            setAutosaveStatus('error');
            return false;
        }
        if (discardRef.current || !dirtyRef.current) {
            resetSavingStatus();
            return true;
        }
        if (autosaveTimerRef.current) {
            window.clearTimeout(autosaveTimerRef.current);
            autosaveTimerRef.current = null;
        }
        // Waits out any already-running save, then runs the DRAFT autosave if
        // still dirty (the wait may have already captured the latest content).
        const waitThenAutosaveIfDirty = async (): Promise<void> => {
            if (savingRef.current && inFlightSaveRef.current) await inFlightSaveRef.current;
            if (dirtyRef.current) await runAutosave();
        };
        await waitThenAutosaveIfDirty();
        if (dirtyRef.current && savingRef.current && inFlightSaveRef.current) {
            // savingRef was re-taken by a DIFFERENT save (Rename/Save/another
            // autosave tick) racing in right as runAutosave tried to start —
            // it saw savingRef.current already true and rescheduled instead
            // of persisting. Retry once for a genuine scheduling collision
            // (savingRef/inFlightSaveRef both still set), rather than
            // reporting a spurious failure (the save-failed dialog) for it — a
            // real save error instead leaves both cleared (runAutosave's own
            // catch), so this guard never masks it with a silent second
            // attempt.
            await waitThenAutosaveIfDirty();
        }
        resetSavingStatus(); // no-op if runAutosave above already moved it on
        return !dirtyRef.current;
    }, [assesseeOrgId, sheetIsLoaded, runAutosave]);

    /**
     * "Close without saving" (the save-failed dialog) — the one path that
     * ever discards unsaved content. Cancels any pending debounce so a
     * background tick can't race this, then sets `discardRef` (flush and the
     * unmount safety net both skip from here on) and clears `dirtyRef`.
     */
    const discardPendingChanges = useCallback(() => {
        if (autosaveTimerRef.current) {
            window.clearTimeout(autosaveTimerRef.current);
            autosaveTimerRef.current = null;
        }
        discardRef.current = true;
        markClean();
    }, [markClean]);

    const { closeFailedOpen, setCloseFailedOpen, commitEditAndClose, handleRetryCloseFailed, handleCloseWithoutSaving } = useSheetCloseFlow(
        {
            onClose,
            workbookRef,
            flushPendingAutosave,
            discardPendingChanges,
        },
    );

    /**
     * Pass to <Workbook onOp>: marks the workbook dirty and (re)schedules the
     * debounced autosave — but ONLY when `ops` actually contains real content
     * (see isContentOp). Fortune-Sheet's onOp fires for everything that goes
     * through its reducer, including tab switches, cell-click/selection, and
     * scrolling — none of those may arm the autosave timer.
     *
     * Dirty tracking runs in EVERY context (it feeds the view contexts' "Add to
     * Workspace" snapshot and close guard); only the autosave is gated on a
     * save context.
     */
    const markWorkbookDirty = useCallback(
        (ops: Op[]) => {
            if (sizeReadOnlyRef.current) return; // size-capped read-only workbook — mounted allowEdit={false}, never dirty
            if (historyOpenRef.current) return; // previewing a historical version is read-only — never dirties the live sheets
            if (suppressDirtyRef.current) return; // hydration stub-fill in flight — path-identical to a real edit
            if (!hasContentOp(ops)) return;
            editGenerationRef.current += 1;
            if (!editedSinceOpenRef.current) {
                editedSinceOpenRef.current = true;
                setHasEditedSinceOpen(true);
            }
            if (!dirtyRef.current) setHasUnsavedEdits(true);
            dirtyRef.current = true;
            if (!assesseeOrgId) return; // view context (Data Room / Magic Upload / Ask AICA) — no autosave
            if (autosaveTimerRef.current) window.clearTimeout(autosaveTimerRef.current);
            autosaveTimerRef.current = window.setTimeout(() => runAutosaveRef.current(), SHEET_AUTOSAVE_DEBOUNCE_MS);
        },
        [assesseeOrgId],
    );

    // Safety net for any unmount NOT already covered by an explicit
    // awaited flush (route change, parent re-key) — the close paths
    // (flushPendingAutosave) handle ×/backdrop/idle-Esc themselves. A LAYOUT
    // effect cleanup, not a passive one: SheetEditorDrawer's own layout
    // destroy runs before its child Workbook's imperative-handle destroy in
    // React 18's deletion order (pinned empirically), so
    // `workbookRef.current` is still live here — unlike the old passive-
    // effect cleanup, which ran after Fortune had already nulled it and fell
    // back to the as-loaded `effectiveSheets`. Snapshots the LIVE
    // sheets synchronously and fires the DRAFT save WITHOUT awaiting it (a
    // cleanup can't keep the component alive for an async op); persistSheet's
    // own hard-rule guard (above) refuses to save if that snapshot is
    // unavailable — it never falls back to effectiveSheets.
    useLayoutEffect(
        () => () => {
            if (autosaveTimerRef.current) window.clearTimeout(autosaveTimerRef.current);
            if (autosaveStatusDecayRef.current) window.clearTimeout(autosaveStatusDecayRef.current);
            // Persists exactly what waiting/runAutosave would (persistSheet
            // mints a fresh lineage when `id` is absent) rather than also
            // requiring liveDocIdRef.current on its own — same reasoning as
            // flushPendingAutosave above. This cleanup can't `await` (it
            // can't keep the component alive for an async op), but the save
            // below is still CHAINED onto awaitPendingDocId via `.then()` —
            // a still-resolving eager import save is waited out first, same
            // as the flush, so this never fires an id-less DRAFT behind its
            // own eventual READY save.
            if (!dirtyRef.current || !assesseeOrgIdRef.current || !sheetIsLoadedRef.current || historyOpenRef.current || discardRef.current)
                return;
            // Same blank-new-sheet exclusion as flushPendingAutosave
            // — see its own comment. isBlankNewSheetRef is read here (not the
            // raw prop) since this cleanup is captured once at mount ([]-deps).
            if (!liveDocIdRef.current && isBlankNewSheetRef.current) return;
            const live = workbookRef.current?.getAllSheets();
            if (!live || !live.length) return; // already gone — nothing safe to snapshot
            const snapshot = getFullSheets(live);
            const run = (): Promise<any> =>
                awaitPendingDocId().then(() =>
                    persistSheetRef.current(
                        'DRAFT',
                        {
                            ...(currentNameRef.current ? { name: currentNameRef.current } : {}),
                            ...(currentDescriptionRef.current ? { description: currentDescriptionRef.current } : {}),
                        },
                        { silent: true, liveSheets: snapshot },
                    ),
                );
            // An autosave with an OLDER snapshot may still be mid-upload —
            // start this one only after it settles, or whichever PUT finishes
            // last (not necessarily this one) would win and could overwrite
            // the newer content with the stale one.
            (inFlightSaveRef.current ? inFlightSaveRef.current.finally(run) : run()).catch((err) => {
                // Fire-and-forget from a cleanup — never left as an unhandled
                // rejection. Logs the error only, never sheet content/file
                // name/URL (mirrors handleSave/runAutosave's own logging).
                console.error('[SheetEditor] unmount safety-net save failed', err);
            });
        },
        [],
    );

    /**
     * Restore = persist the previewed version as a NEW committed version in the
     * SAME lineage (fork: omit `id`, keep `genId`) — mirrors
     * ReportEditorRoute.handleRestore. The version's bytes already live in S3
     * as an immutable object version, so `storageOverride` re-points the new
     * row at them — no re-serialize, no re-upload. Reuses persistSheet/
     * handleSave's saving/savingRef so it can never race a concurrent Save or
     * Rename.
     */
    const handleRestoreVersion = useCallback(
        async (sheetsToRestore: FortuneSheet[], storageOverride?: SheetStorageRef) => {
            if (!assesseeOrgId) return;
            if (!Array.isArray(sheetsToRestore) || !sheetsToRestore.length) return;
            // Looped, same as handleSave — never claim savingRef off a shared release.
            while (savingRef.current && inFlightSaveRef.current) await inFlightSaveRef.current;
            savingRef.current = true;
            setSaving(true);
            const op = (async () => {
                try {
                    await persistSheet(
                        'READY',
                        {
                            ...(currentName ? { name: currentName } : {}),
                            ...(currentDescription ? { description: currentDescription } : {}),
                        },
                        { fork: true, isRestored: true, sheetsOverride: sheetsToRestore, storageOverride },
                    );
                    markClean();
                    // The restored version takes over as the live editable content,
                    // and the history pane closes in the SAME update batch (its
                    // onDrawerClose clears preview state + the version cache), so
                    // the workbook remounts exactly once — EDITABLE. Remounting
                    // read-only (allowEdit=false, pane still open) is what crashed
                    // here: Fortune-Sheet's read-only InputBox layout effect and its
                    // groupValuesRefresh formula drain re-trigger each other past
                    // React's nested-update limit on formula-heavy workbooks.
                    setRestoredSheets(sheetsToRestore);
                    setRestoreVersion((v) => v + 1);
                    // Cleared BEFORE closing the pane — onDrawerClose's own
                    // snapshot-restore would otherwise clobber the version
                    // just restored above with whatever was live before the
                    // preview that preceded this restore.
                    liveSnapshotRef.current = null;
                    closeHistoryRef.current?.();
                    toast.success('Version restored');
                } catch (err) {
                    console.error('[useSheetEditorDrawerLogic] restore failed', err);
                    toast.error('Could not restore this version. Please try again.');
                } finally {
                    setSaving(false);
                    setSavingPhase('idle');
                    savingRef.current = false;
                    inFlightSaveRef.current = null;
                }
            })();
            inFlightSaveRef.current = op;
            await op;
        },
        [assesseeOrgId, currentName, currentDescription, persistSheet, markClean],
    );

    // Version history — reuses the shared HistorySideDrawer shell/UX (see
    // ReportEditorRoute for the reference wiring). Row click previews a version
    // in place (read-only, see previewSheets); the pane's footer Restore button
    // (restoreInFooter mode) → confirm modal → onSelectEntry restores it.
    const historyDrawer = useHistoryDrawer({
        documentType: 'REPORTS',
        fetchHistoryFn: async () => {
            const genId = liveGenIdRef.current;
            if (!genId || !assesseeOrgId) return [];
            const versions = await getDocumentHistory({ assesseeOrgId, genId });
            // Drafts are the working copy, never history.
            return versions
                .filter((v) => !v.isDraft)
                .map(
                    (v): HistoryEntry => ({
                        _id: String(v.id ?? v._id ?? ''),
                        userName: v.lastEditedBy ?? v.createdBy ?? '',
                        lastUpdatedAt: v.lastUpdatedAt ?? v.createdAt ?? 0,
                        isRestored: Boolean(v.isRestored),
                    }),
                );
        },
        fetchVersionFn: async (entryId: string) => {
            // A committed version's content never changes after creation, so
            // re-clicking the currently-viewed row renders instantly with zero
            // network. The cache is replaced (not grown) per entry — see
            // versionCacheRef.
            const cached = versionCacheRef.current.get(entryId);
            if (cached) return cached.sheets;

            const doc = (await getDocument({ docId: entryId, assesseeOrgId: assesseeOrgId as string })) as any;
            const ref: SheetStorageRef | undefined = isSheetStorageRef(doc?.storage) ? doc.storage : undefined;
            let versionSheets: FortuneSheet[];
            if (ref) {
                const url = await getSheetDownloadUrl(ref);
                const response = await fetch(url);
                const buffer = await response.arrayBuffer();
                versionSheets = await parseSheetBytes(buffer, undefined, fileName, { context: canSave ? 'editor' : 'view' });
            } else {
                versionSheets = Array.isArray(doc?.content?.value) ? doc.content.value : [];
            }
            versionCacheRef.current = new Map([[entryId, { sheets: versionSheets, storage: ref }]]);
            return versionSheets;
        },
        onVersionLoaded: (data, shouldSave, entryId, isInitialLoad) => {
            if (shouldSave) {
                handleRestoreVersion(Array.isArray(data) ? data : [], entryId ? versionCacheRef.current.get(entryId)?.storage : undefined);
                return;
            }
            // A version fetch that resolves after the pane closed is a full
            // no-op: no editable old version, no stale snapshot or selection.
            // (The initial load runs before historyOpenRef's effect has landed.)
            if (!isInitialLoad && !historyOpenRef.current) return;
            setPreviewEntryId(entryId ?? null);
            if (isInitialLoad) return; // the editor already shows the live/latest version
            const isLatest = historyEntriesRef.current[0]?._id === entryId;
            if (isLatest) {
                // Selecting the latest row again while History stays open
                // (latest -> older -> latest) must still show the live edits
                // captured before the FIRST older-version preview — clearing
                // only previewSheets here would fall through to
                // `restoredSheets ?? sheets`, and restoredSheets is otherwise
                // only ever set on close, so this would remount from the
                // as-loaded `sheets` instead. The ref is NOT cleared: picking
                // an older version again afterwards must still have the
                // original snapshot available to restore from on close.
                if (liveSnapshotRef.current) setRestoredSheets(liveSnapshotRef.current);
                setPreviewSheets(null);
                return;
            }
            // The live workbook is still mounted here (the preview swap below
            // hasn't happened yet this call) — snapshot it once, the first
            // time a non-latest preview is about to replace it, so closing
            // the pane (or browsing to another version) can remount from the
            // session's own edits instead of the as-loaded `sheets`.
            if (!liveSnapshotRef.current && canSave) liveSnapshotRef.current = currentSheets().map(toCelldataSheet);
            setPreviewSheets(stripFormulasForPreview(Array.isArray(data) ? data : []));
        },
        onDrawerClose: () => {
            // Synchronously, ahead of the effect below: a fetch resolving
            // between the close click and React's commit must see it closed.
            historyOpenRef.current = false;
            // Return to the live editable content — remounting from the
            // pre-preview snapshot (if one was taken) rather than the
            // as-loaded `sheets`, since Fortune only reads `data` at init and
            // the live sheets WERE swapped out behind the preview.
            if (liveSnapshotRef.current) {
                setRestoredSheets(liveSnapshotRef.current);
                liveSnapshotRef.current = null;
            }
            setPreviewSheets(null);
            setPreviewEntryId(null);
            versionCacheRef.current.clear();
        },
    });

    useEffect(() => {
        historyOpenRef.current = historyDrawer.isOpen;
    }, [historyDrawer.isOpen]);
    useEffect(() => {
        historyEntriesRef.current = historyDrawer.historyEntries;
    }, [historyDrawer.historyEntries]);
    useEffect(() => {
        closeHistoryRef.current = historyDrawer.closeDrawer;
    }, [historyDrawer.closeDrawer]);

    /**
     * Opening Version History must never show a preview
     * alongside a still-pending edit — commit any in-progress cell edit
     * (same mechanism every other close/save path uses), then AWAIT the
     * flush before opening. A failed flush leaves history closed, in the
     * drawer's existing "Save failed" state — mirrors every other flush-
     * gated action. Closing history needs neither step.
     */
    // Mirrors SheetEditorDrawer.tsx's closingRef — a second click arriving
    // while the commit+flush for the first is still pending must not call
    // openDrawer() a second time.
    const openingHistoryRef = useRef(false);
    const toggleHistoryDrawer = useCallback(async () => {
        if (sizeReadOnlyRef.current) return; // a second parsed copy of a size-capped workbook would exhaust the tab's memory
        if (historyDrawer.isOpen) {
            historyDrawer.closeDrawer();
            return;
        }
        if (openingHistoryRef.current) return;
        openingHistoryRef.current = true;
        try {
            await commitEditThen(getInternals(workbookRef.current), async () => {
                if (await flushPendingAutosave()) await historyDrawer.openDrawer();
            });
        } finally {
            openingHistoryRef.current = false;
        }
    }, [historyDrawer.isOpen, historyDrawer.closeDrawer, historyDrawer.openDrawer, flushPendingAutosave]);

    return {
        /** Whether the caller passed a persistence context (Reports) — the single
         *  source of truth for whether the grid may be edited/saved. */
        canSave,
        sheets,
        /** What <Workbook data> should mount: the live sheets, or a historical
         *  preview's sheets — either way, full or active + stubs for large workbooks. */
        workbookSheets,
        loading,
        loadingStage,
        error,
        /** Set instead of `error` when the size guard refused the file. */
        tooLarge,
        /** True while the size guard's 500k-2M "large" notice should show. */
        largeNotice,
        saving,
        savingPhase,
        /** Background autosave indicator — never gates the Save & Close button. */
        autosaveStatus,
        workbookRef,
        handleDownloadXlsx,
        handleDownloadCsv,
        handleSave,
        hasPendingChanges,
        /** Awaited flush of a pending DRAFT autosave — every non-cancel
         *  close path (×, backdrop, idle Esc) calls this before `onClose`. */
        flushPendingAutosave,
        /** "Close without saving" (the save-failed dialog) — see its own doc comment. */
        discardPendingChanges,
        /** A failed close-flush opens the save-failed dialog instead of leaving
         *  the drawer silently open. */
        closeFailedOpen,
        setCloseFailedOpen,
        /** ×/backdrop/idle-Esc close wiring (useSheetCloseFlow). */
        commitEditAndClose,
        /** The save-failed dialog's Retry / "Close without saving" actions. */
        handleRetryCloseFailed,
        handleCloseWithoutSaving,
        markWorkbookDirty,
        /** True when the workbook has more filled cells than MAX_EDITABLE_CELLS —
         *  mounted `allowEdit={false}`, never saved or serialised. */
        sizeReadOnly,
        /** Filled-cell count the read-only decision and its copy use. */
        filledCells,
        /** Whether a save/rename can proceed read-only (stored bytes or an .xlsx original). */
        canPersistReadOnly,
        originalIsXlsx,
        readOnlyAddBand,
        canDownloadOriginal,
        /** Download for a read-only workbook (and the too-large screen): the original bytes. */
        handleDownloadOriginal,
        /** True from the first content edit until a save / Add to Workspace succeeds. */
        hasUnsavedEdits,
        /** True from the first content edit until the drawer closes — a save / add never resets it. */
        hasEditedSinceOpen,
        /** Monotonic content-edit counter (selection / history-preview / hydration ops excluded). */
        getEditVersion,
        /** View contexts call this once the host confirms the edited snapshot reached Workspace. */
        clearUnsavedEdits: markClean,
        /** The sheets exactly as shown right now (live workbook, stubs resolved) —
         *  what Download and the edited Add-to-Workspace snapshot both read. */
        getCurrentSheets: currentSheets,
        currentName,
        currentDescription,
        handleRename,
        /**
         * Gates the header's History button: a genId lineage must exist AND the
         * document's bytes must be individually addressable.
         *
         * On a bucket without object versioning a save overwrites the one object,
         * so every version of the lineage resolves to the SAME (current) bytes.
         * Listing them would offer the user versions that all render identically
         * — worse, previewing an old entry would silently show today's content.
         * A version-less storage ref is exactly that signal, so history stays
         * hidden and the document reads as the single current version it is.
         * Inline (non-S3) documents keep their history, which is Mongo-backed.
         */
        hasGenId: Boolean(liveGenIdRef.current) && (storageRef.current == null || hasVersionHistory(storageRef.current)),
        historyOpen: historyDrawer.isOpen,
        historyExpanded: historyDrawer.isExpanded,
        historyLoading: historyDrawer.loading,
        historyEntries: historyDrawer.historyEntries,
        historyPreviewLoading: historyDrawer.previewLoading,
        /** Flushes any pending autosave BEFORE opening (never before
         *  closing) — see toggleHistoryDrawer's own doc comment above. */
        toggleHistoryDrawer,
        closeHistoryDrawer: historyDrawer.closeDrawer,
        toggleHistoryExpand: historyDrawer.toggleExpand,
        /** Row click → in-place read-only preview. */
        previewHistoryEntry: historyDrawer.previewHistoryEntry,
        /** 3-dot menu confirm → restore (fork + persist). */
        selectHistoryEntry: historyDrawer.selectHistoryEntry,
        /** The history row currently selected/highlighted (incl. the auto-selected
         *  latest on pane open, which does NOT swap content). */
        previewEntryId,
        /** True only while a historical version's sheets are actually mounted in
         *  place of the live workbook — the only preview state that requires a
         *  Workbook remount. Row selection alone (previewEntryId) must not. */
        previewActive: Boolean(previewSheets),
        /** Bumped on every successful restore — fold into the Workbook `key` so it
         *  remounts even when previewEntryId doesn't change (a direct 3-dot restore). */
        restoreVersion,
    };
};
